import { ReactNode, SyntheticEvent, useEffect, useState } from 'react';
import TextField from '@mui/material/TextField';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import Autocomplete, { AutocompleteRenderInputParams } from '@mui/material/Autocomplete';
import { Box, Checkbox } from '@mui/material';
import { BodyText } from '../../typography/PrimaryTypography';
import { formatTitleCase } from '../../../../util/general_utils';

interface BasicSelectProps {
  handleChangeSelection: (selected_options: Array<string> | string | null) => void;
  options?: Array<string>;
  selected_options?: Array<string> | string | null;
  disabled?: boolean;
  input_label?: string;
  clearable?: boolean;
  multi_select?: boolean;
  tag_limit?: number;
  searchable?: boolean;
  color?: string;
  disable_underline?: boolean;
  input_icon?: ReactNode;
  disable_selected_tags?: boolean;
  select_all?: boolean;
  option_label_title_case?: boolean;
}

// Checkbox icons
const check_icon_blank: JSX.Element = <CheckBoxOutlineBlankIcon fontSize="small" />;
const check_icon_filled: JSX.Element = <CheckBoxIcon fontSize="small" />;

/**
 * General text item selection auto-complete component that handles a variety of built-in scenarios.
 * @param {(selected_options: Array<string> | string | null): void} props.handleChangeSelection Notify the parent component when the selected options have changed.
 * @param {Array<string>} props.options Custom list of options provided by the parent component. Defaults to none.
 * @param {string | null} props.selected_options Custom list of selected options provided by the parent component. Changes component from an uncontrolled to controlled.
 * @param {boolean} props.disabled Renders the component in a disabled state along with any other provided components. Defaults to enabled.
 * @param {string} props.input_label Sets a custom component input label. Defaults to the singular version of the main entity name.
 * @param {boolean} props.clearable Allows the autocomplete to be cleared. Defaults to true.
 * @param {boolean} props.multi_select Allow multi option selection and keeps the select open while selecting. Defaults to a single select and closes after selecting an option.
 * @param {number} props.tag_limit Sets a limit on the amount of tags rendered in the input. Defaults to unlimited.
 * @param {boolean} props.searchable Allows the user to type to search in the autocomplete. Defaults to true.
 * @param {string} props.color A custom color used for the input component of the autocomplete. Defaults to standard MUI color.
 * @param {string} props.disable_underline Allows the hiding of the underline on a standard variant text field.
 * @param {ReactNode} props.input_icon Optionally provide an icon to display next to the input label.
 * @param {boolean} props.disable_selected_tags Optionally replace the selected tags with text when autocomplete is expanded to render input at a predictable height.
 * @returns {JSX.Element}
 */
const BasicSelect = (props: BasicSelectProps) => {
  const [optionList, setOptions] = useState<Array<string>>([]);
  const [selectedOptions, setSelectedOptions] = useState<Array<string> | string | null>(
    props.multi_select ? [] : null
  );

  // Define autocomplete options
  useEffect(() => {
    (async () => {
      const options: Array<string> = props.options
        ? // Options supplied by the parent component.
          props.options
        : [];
      setOptions(options);
    })();
  }, [props.options]);

  // Define selected options
  useEffect(() => {
    setSelectedOptions(
      props.multi_select
        ? Array.isArray(props.selected_options)
          ? props.selected_options
          : []
        : props.selected_options
          ? props.selected_options
          : null
    );
  }, [props.options, props.selected_options]);

  const checkAll = (): boolean => {
    if (Array.isArray(selectedOptions)) {
      return optionList.length === selectedOptions.length;
    }
    return false;
  };

  const changeAll = (): void => {
    if (Array.isArray(selectedOptions)) {
      if (selectedOptions.length > 0) {
        setSelectedOptions([]);
        props.handleChangeSelection && props.handleChangeSelection([]);
      } else {
        setSelectedOptions(optionList);
        props.handleChangeSelection && props.handleChangeSelection(optionList);
      }
    }
  };

  return (
    <Autocomplete
      id="basic-select"
      fullWidth
      multiple={props.multi_select}
      limitTags={props.tag_limit}
      disableCloseOnSelect={props.multi_select}
      disabled={props.disabled}
      disableClearable={props.clearable === false}
      options={optionList}
      value={selectedOptions}
      getOptionLabel={(option: string) => {
        return props.option_label_title_case ? formatTitleCase(option) : option;
      }}
      isOptionEqualToValue={(option: string, value: string) => option === value}
      noOptionsText={`No Options`}
      renderOption={(render_props, option: string, { selected }) => (
        <Box
          component="li"
          sx={{ display: 'flex', alignItems: 'center' }}
          {...render_props}
          key={option}
        >
          {props.multi_select && (
            <Checkbox
              icon={check_icon_blank}
              checkedIcon={check_icon_filled}
              style={{ marginRight: 8 }}
              checked={selected}
            />
          )}
          {props.option_label_title_case ? formatTitleCase(option) : option}
        </Box>
      )}
      renderValue={
        !props.multi_select || !props.disable_selected_tags
          ? undefined
          : (value: string | Array<string>) => {
              return Array.isArray(value) && value.length > 0
                ? `${value.length} Selected`
                : undefined;
            }
      }
      renderInput={(params: AutocompleteRenderInputParams) => (
        <TextField
          {...params}
          label={
            props.input_icon ? (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                {props.input_icon}
                <BodyText>{props.input_label ?? 'Select Options'}</BodyText>
              </Box>
            ) : (
              (props.input_label ?? 'Select Options')
            )
          }
          variant="standard"
          slotProps={{
            htmlInput: { ...params.inputProps },
            input: {
              ...params.InputProps,
              endAdornment: (
                <>
                  {props.select_all && props.multi_select && (
                    <Box component="div">
                      <Box
                        component="li"
                        style={{
                          fontSize: '0.85rem',
                          color: '#616161',
                          fontWeight: 500
                        }}
                      >
                        <Checkbox
                          icon={check_icon_blank}
                          checkedIcon={check_icon_filled}
                          checked={checkAll()}
                          onChange={() => {
                            changeAll();
                          }}
                        />
                        Select All
                      </Box>
                    </Box>
                  )}
                  {params.InputProps.endAdornment}
                </>
              )
            }
          }}
          inputProps={{ ...params.inputProps, readOnly: props.searchable === false }}
          InputProps={{ ...params.InputProps, disableUnderline: props.disable_underline }}
          sx={{
            input: { color: props.color ? props.color : 'currentcolor' },
            '& .MuiSvgIcon-root': {
              color: props.color ? props.color : 'currentcolor'
            }
          }}
        />
      )}
      onChange={(event: SyntheticEvent<Element, Event>, value: Array<string> | string | null) => {
        setSelectedOptions(value);
        props.handleChangeSelection(value);
      }}
    />
  );
};

export default BasicSelect;
